import * as moment from 'moment';
import * as React from 'react';
import * as ReactPaginate from 'react-paginate';
import TableSearch from './search';
import TableBodyRowObject from './tableBodyRowObject';
import TableHeadRowObject from './tableHeadRowObject';
import { DataType, RowObject, Tbody } from './tableModels';

interface TableProps {
    tHead?: RowObject[];
    tBody: Tbody[];
    listSize: number;
    header?: string;
    initialSort?: number;
    search?: boolean;
    placeholder?: string;
    striped?: boolean;
    hover?: boolean;
    cursor?: boolean;
    altCSS?: string;
    previousLabel?: string;
    nextLabel?: string;
    clearButtonLable?: string;
    noSort?: boolean;
    hidePagination?: boolean;
    americanDateFormat?: boolean;
    tableFunction?: (id: number) => void;
}

interface PageChange {
    selected: number;
}

interface TableState {
    sortIndex: number;
    tBody: Tbody[];
    reverse: boolean;
    done: boolean;
    start: number;
    tableCSS: string;
}

class Table extends React.Component<TableProps, TableState> {
    constructor(props: TableProps) {
        super(props);
        this.state = {
            sortIndex: -1,
            tBody: new Array<Tbody>(),
            reverse: false,
            done: true,
            start: 0,
            tableCSS: ''
        };
        this.renderHead = this.renderHead.bind(this);
        this.renderBody = this.renderBody.bind(this);
        this.sortOnThis = this.sortOnThis.bind(this);
        this.sortOnIndex = this.sortOnIndex.bind(this);
        this.pageClicked = this.pageClicked.bind(this);
        this.onClickRow = this.onClickRow.bind(this);
        this.onSearch = this.onSearch.bind(this);
        this.getPlaceHolder = this.getPlaceHolder.bind(this);
        this.isAmericanDate = this.isAmericanDate.bind(this);
    }

    componentWillMount() {
        let cssString = 'table';
        if (this.props.cursor && this.props.cursor === true) {
            cssString += ' tableCurser';
        }
        if (this.props.hover && this.props.hover === true) {
            cssString += ' table-hover';
        }
        if (this.props.striped && this.props.striped === true) {
            cssString += ' table-striped';
        }
        if (this.props.altCSS) {
            cssString += ' ' + this.props.altCSS;
        }
        if (this.props.initialSort) {
            this.setState({ tBody: this.props.tBody, sortIndex: this.props.initialSort, tableCSS: cssString });
        } else {
            this.setState({ tBody: this.props.tBody, tableCSS: cssString });
        }
    }

    componentDidMount() {
        if (this.props.initialSort) {
            this.setState({ tBody: this.state.tBody.sort(this.sortOnIndex) });
        }
    }

    getPlaceHolder() {
        if (this.props.placeholder) {
            return this.props.placeholder;
        } else {
            return 'Search';
        }
    }

    componentWillReceiveProps(nextProps: TableProps) {
        const updatedState = Object.assign({}, this.state, { tBody: nextProps.tBody });
        this.setState(updatedState);
    }

    componentDidUpdate(prevProps: TableProps, prevState: TableState) {
        if (!this.state.done) {
            if (prevState.reverse !== this.state.reverse) {
                if (this.state.reverse) {
                    this.setState({ tBody: this.state.tBody.sort(this.sortOnIndex).reverse() });
                } else {
                    this.setState({ tBody: this.state.tBody.sort(this.sortOnIndex) });
                }
            }
            this.setState({ done: true });
        }
    }

    pageClicked(page: PageChange) {
        this.setState({ start: page.selected * this.props.listSize });
    }

    onClickRow(x: Tbody) {
        if (x.rowFunction && x.rowIdentifier) {
            x.rowFunction(String(x.rowIdentifier));
        } else {
            if (this.props.tableFunction) {
                this.props.tableFunction(Number(x.rowIdentifier));
            }
        }
    }

    sortOnIndex(a: Tbody, b: Tbody) {
        if (a.rowBody[this.state.sortIndex] === undefined && b.rowBody[this.state.sortIndex] !== undefined) {
            return -1;
        } else if (b.rowBody[this.state.sortIndex] === undefined && a.rowBody[this.state.sortIndex] !== undefined) {
            return 1;
        } else if (b.rowBody[this.state.sortIndex] === undefined && a.rowBody[this.state.sortIndex] === undefined) {
            return 0;
        } else {
            // Compare Date if American Date Format
            if (
                typeof a.rowBody[this.state.sortIndex].obj === 'string' &&
                typeof b.rowBody[this.state.sortIndex].obj === 'string' &&
                this.props.americanDateFormat === true
            ) {
                if (
                    this.isAmericanDate(String(a.rowBody[this.state.sortIndex].obj)) &&
                    this.isAmericanDate(String(b.rowBody[this.state.sortIndex].obj))
                ) {
                    const aDate = moment(String(a.rowBody[this.state.sortIndex].obj), 'M/D/YYYY');
                    const bDate = moment(String(b.rowBody[this.state.sortIndex].obj), 'M/D/YYYY');
                    return aDate < bDate ? -1 : aDate > bDate ? 1 : 0;
                }
            }

            const x = a.rowBody[this.state.sortIndex].obj;
            const y = b.rowBody[this.state.sortIndex].obj;
            return x < y ? -1 : x > y ? 1 : 0;
        }
    }

    isAmericanDate(date: string) {
        const parts = date.split('/');
        if (parts.length === 3) {
            const month = parseInt(parts[0], 10);
            const day = parseInt(parts[1], 10);
            const year = parseInt(parts[2], 10);

            if (month > 0 && month < 13 && day > 0 && day < 32 && year > 1800 && year < 2100) {
                return true;
            }
        }
        return false;
    }

    sortOnThis(index: number) {
        if (this.props.noSort !== true) {
            if (index !== this.state.sortIndex) {
                this.setState({ sortIndex: index, reverse: !this.state.reverse, done: false }); // Issue when it gets here.
            } else {
                this.setState({ reverse: !this.state.reverse, done: false });
            }
        }
    }

    renderHead(props: TableProps) {
        const headRow: JSX.Element[] = Array<any>();
        let caret: string = 'caret-down';
        if (!this.state.reverse) {
            caret = 'caret-up';
        }
        if (props.tHead && props.tHead.length > 0) {
            for (const x in props.tHead) {
                if (this.state.sortIndex > -1) {
                    headRow.push(
                        <TableHeadRowObject
                            key={'head1' + x}
                            object={props.tHead[x]}
                            index={x}
                            sortingIndex={this.state.sortIndex}
                            sortOnThis={this.sortOnThis}
                            icon={Number(x) === this.state.sortIndex ? caret : ''}
                            cssClassName={props.tHead[x].cssClassName ? props.tHead[x].cssClassName : ''}
                        />
                    );
                } else {
                    headRow.push(
                        <TableHeadRowObject
                            key={'head2' + x}
                            object={props.tHead[x]}
                            index={x}
                            sortingIndex={this.state.sortIndex}
                            sortOnThis={this.sortOnThis}
                            icon={''}
                            cssClassName={props.tHead[x].cssClassName ? props.tHead[x].cssClassName : ''}
                        />
                    );
                }
            }
        }
        return headRow;
    }

    renderBody(tBody: Array<Tbody>) {
        const tableBody: JSX.Element[] = Array<any>();
        if (tBody && tBody.length > 0) {
            for (const x in tBody) {
                if (Number(x) > this.state.start - 1 && Number(x) < this.props.listSize + this.state.start) {
                    const bodyRow: JSX.Element[] = Array<any>();
                    for (const y of tBody[x].rowBody) {
                        {
                            y.cssClassName
                                ? bodyRow.push(
                                      <TableBodyRowObject
                                          key={'tbody1' + x + bodyRow.length}
                                          object={y}
                                          cssClassName={y.cssClassName}
                                      />
                                  )
                                : bodyRow.push(<TableBodyRowObject key={'tbody2' + x + bodyRow.length} object={y} />);
                        }
                    }
                    if (tBody[x].rowBody.length === 0) {
                        bodyRow.push(
                            <TableBodyRowObject key={'tbody3' + x} object={new RowObject(DataType.string, '')} />
                        );
                    }
                    if (tBody[x]) {
                        tableBody.push(
                            <tr key={'tbody4' + x} onClick={this.onClickRow.bind(this, tBody[x])}>
                                {bodyRow}
                            </tr>
                            // ayy here
                        );
                    } else {
                        tableBody.push(
                            <tr key={'tbody5' + x} onClick={this.onClickRow.bind(this, tBody[x])}>
                                {bodyRow}
                            </tr>
                        );
                    }
                    if (tBody[x].descriptionRow) {
                        tableBody.push(
                            <tr key={'description' + x} onClick={this.onClickRow.bind(this, tBody[x])}>
                                <th className="descriptonTD" colSpan={1}>
                                    {tBody[x].descriptionRow!.lable}
                                </th>
                                <td className="descriptonTD" colSpan={tBody[x].rowBody.length}>
                                    {tBody[x].descriptionRow!.description}
                                </td>
                            </tr>
                        );
                    }
                }
            }
        }
        return tableBody;
    }
    /* tslint:disable */
    onSearch(searchTerm: string | number) {
        let newTbody = new Array<any>();

        for (let x of this.state.tBody) {
            let returnMe: boolean = false;
            for (let y of x.rowBody) {
                if (y.obj.toString().toLowerCase().indexOf(searchTerm.toString().toLowerCase()) !== -1) {
                    returnMe = true;
                }
            }
            if (returnMe) {
                newTbody.push(x);
            }
        }
        this.setState({ tBody: newTbody });
        this.pageClicked({ selected: 1 });
        this.setState({ start: 0 });
    }

    public render() {
        return (
            <div className="tableContainer">
                <div className="float-left bigHeader">{this.props.header}</div>
                <div className="float-right">
                    {Math.ceil(this.state.tBody.length / this.props.listSize) > 1 ||
                    this.props.hidePagination !== true ? (
                        <ReactPaginate
                            previousLabel={this.props.previousLabel ? this.props.previousLabel : 'Previous'}
                            nextLabel={this.props.nextLabel ? this.props.nextLabel : 'Next'}
                            breakLabel={<a>...</a>}
                            breakClassName={'page-link paginationItem'}
                            pageCount={Math.ceil(this.state.tBody.length / this.props.listSize)}
                            marginPagesDisplayed={1}
                            pageRangeDisplayed={5}
                            onPageChange={this.pageClicked}
                            containerClassName={'pagination'}
                            activeClassName={'page-item activePaginationItem'}
                            pageLinkClassName={'page-link paginationItem'}
                            previousLinkClassName={'page-link paginationItem'}
                            nextLinkClassName={'page-link paginationItem'}
                        />
                    ) : (
                        <></>
                    )}
                </div>
                {this.props.search !== null && this.props.search === true ? (
                    <TableSearch
                        onSearch={this.onSearch}
                        searchPlaceHolder={this.getPlaceHolder()}
                        clearButton={this.props.clearButtonLable !== undefined ? this.props.clearButtonLable : 'Clear'}
                    />
                ) : (
                    <></>
                )}

                <table className={this.state.tableCSS}>
                    <thead>
                        <tr>{this.renderHead(this.props)}</tr>
                    </thead>
                    <tbody>{this.renderBody(this.state.tBody)}</tbody>
                </table>
            </div>
        );
    }
}

export default Table;



// WEBPACK FOOTER //
// ./src/components/table/table.tsx